/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Knowledge Base & Vectorize Pipeline State Store (Task 10.3.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export interface KnowledgeChunkItem {
  id: string;
  documentId: string;
  chunkIndex: number;
  tokenCount: number;
  content: string;
  metadata: {
    page?: number;
    heading?: string;
    wordCount?: number;
  };
}

export interface KnowledgeDocumentItem {
  id: string;
  workspaceId: string;
  title: string;
  fileName: string;
  fileType: 'pdf' | 'docx' | 'txt' | 'csv' | 'markdown';
  fileSizeBytes: number;
  formattedSize: string;
  status: 'uploading' | 'chunking' | 'vectorizing' | 'active' | 'failed';
  chunkCount: number;
  embeddingDimensions: number; // 1536
  embeddingModel: string;
  r2StorageKey: string;
  vectorizeIndexName: string;
  createdAt: string;
  updatedAt: string;
  sampleChunks: KnowledgeChunkItem[];
  errorMessage?: string;
}

export type KnowledgeFilterStatus = 'all' | 'active' | 'vectorizing' | 'chunking' | 'uploading' | 'failed';
export type KnowledgeFilterType = 'all' | 'pdf' | 'docx' | 'txt' | 'csv' | 'markdown';

export interface KnowledgeState {
  documents: KnowledgeDocumentItem[];
  searchQuery: string;
  filterStatus: KnowledgeFilterStatus;
  filterType: KnowledgeFilterType;
  isUploadModalOpen: boolean;
  selectedPreviewDoc: KnowledgeDocumentItem | null;
  activeReindexingId: string | null;
  reindexProgress: number; // 0 - 100

  // Actions
  addDocuments: (docs: KnowledgeDocumentItem[]) => void;
  updateDocumentStatus: (id: string, status: KnowledgeDocumentItem['status'], errorMessage?: string) => void;
  deleteDocument: (id: string) => void;
  reindexDocument: (id: string) => Promise<void>;
  openChunkPreview: (doc: KnowledgeDocumentItem) => void;
  closeChunkPreview: () => void;
  openUploadModal: () => void;
  closeUploadModal: () => void;
  setSearchQuery: (query: string) => void;
  setFilterStatus: (status: KnowledgeFilterStatus) => void;
  setFilterType: (type: KnowledgeFilterType) => void;
  resetStore: () => void;

  // Selectors
  getFilteredDocuments: () => KnowledgeDocumentItem[];
  getTotalStorageBytes: () => number;
  getTotalChunksCount: () => number;
}

const STORAGE_KEY_KNOWLEDGE = 'sophia_knowledge_documents';

/**
 * Initial sample enterprise documents for RAG Knowledge Base.
 */
export const INITIAL_KNOWLEDGE_DOCUMENTS: KnowledgeDocumentItem[] = [
  {
    id: 'doc_cf_edge_whitepaper',
    workspaceId: 'ws_demo_primary',
    title: 'Cloudflare Edge Architecture Whitepaper 2026',
    fileName: 'Cloudflare_Edge_Architecture_Whitepaper_2026.pdf',
    fileType: 'pdf',
    fileSizeBytes: 2450000,
    formattedSize: '2.4 MB',
    status: 'active',
    chunkCount: 48,
    embeddingDimensions: 1536,
    embeddingModel: '@cf/baai/bge-large-en-v1.5',
    r2StorageKey: 'rag/ws_demo_primary/Cloudflare_Edge_Architecture_Whitepaper_2026.pdf',
    vectorizeIndexName: 'sophia-knowledge-1536',
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    sampleChunks: [
      {
        id: 'chunk_cf_01',
        documentId: 'doc_cf_edge_whitepaper',
        chunkIndex: 0,
        tokenCount: 480,
        content:
          'Cloudflare Workers cung cấp môi trường V8 Isolate siêu nhẹ với thời gian khởi động lạnh (Cold Start) gần như bằng 0 (dưới 5ms trên toàn cầu). Cơ chế này tối ưu hóa việc tiếp nhận các luồng Server-Sent Events (SSE) mà không cần duy trì kết nối WebSocket cồng kềnh.',
        metadata: { page: 1, heading: 'Kiến Trúc Workers Runtime', wordCount: 68 },
      },
      {
        id: 'chunk_cf_02',
        documentId: 'doc_cf_edge_whitepaper',
        chunkIndex: 1,
        tokenCount: 512,
        content:
          'Dữ liệu phân tán trên Cloudflare D1 cho phép đồng bộ hóa dữ liệu giao dịch ACID chuẩn SQLite với độ trễ đọc dưới 10ms thông qua các bản sao biên (Edge Read Replicas). Kết hợp với Vectorize Index 1536 chiều, hệ thống có khả năng truy hồi ngữ nghĩa RAG chuẩn xác với ngưỡng tương đồng Cosine Similarity >= 0.65.',
        metadata: { page: 4, heading: 'Tầng Lưu Trữ & Vectorize RAG', wordCount: 75 },
      },
      {
        id: 'chunk_cf_03',
        documentId: 'doc_cf_edge_whitepaper',
        chunkIndex: 2,
        tokenCount: 420,
        content:
          'Lưu trữ đối tượng Cloudflare R2 loại bỏ hoàn toàn phí Egress (Zero-Egress Fee), cho phép phục vụ tệp PDF và phương tiện truyền thông với độ trễ thấp và băng thông không giới hạn.',
        metadata: { page: 7, heading: 'R2 Object Storage & CDN Caching', wordCount: 54 },
      },
    ],
  },
  {
    id: 'doc_soc2_compliance',
    workspaceId: 'ws_demo_primary',
    title: 'Sophia Platform Security & Compliance SOC2',
    fileName: 'Sophia_Platform_Security_Compliance_SOC2.docx',
    fileType: 'docx',
    fileSizeBytes: 1150000,
    formattedSize: '1.1 MB',
    status: 'active',
    chunkCount: 24,
    embeddingDimensions: 1536,
    embeddingModel: '@cf/baai/bge-large-en-v1.5',
    r2StorageKey: 'rag/ws_demo_primary/Sophia_Platform_Security_Compliance_SOC2.docx',
    vectorizeIndexName: 'sophia-knowledge-1536',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    sampleChunks: [
      {
        id: 'chunk_soc_01',
        documentId: 'doc_soc2_compliance',
        chunkIndex: 0,
        tokenCount: 490,
        content:
          'Chính sách kiểm soát truy cập dựa trên vai trò (RBAC) được thực thi nghiêm ngặt tại tầng biên. Mỗi yêu cầu gọi API đều được xác thực qua JWT mã hóa khóa bất đối xứng và kiểm tra quyền hạn Workspace Member (OWNER, ADMIN, MEMBER, VIEWER).',
        metadata: { page: 2, heading: 'Kiểm Soát Truy Cập RBAC', wordCount: 65 },
      },
      {
        id: 'chunk_soc_02',
        documentId: 'doc_soc2_compliance',
        chunkIndex: 1,
        tokenCount: 460,
        content:
          'Bảo mật mã hóa: Tất cả dữ liệu tĩnh (Data at Rest) trên Cloudflare D1 và R2 đều được mã hóa bằng chuẩn AES-256 GCM. Dữ liệu truyền tải (Data in Transit) bắt buộc sử dụng giao thức TLS 1.3 với chứng chỉ tự động xoay vòng.',
        metadata: { page: 5, heading: 'Chuẩn Mã Hóa Dữ Liệu', wordCount: 60 },
      },
    ],
  },
  {
    id: 'doc_q4_prd',
    workspaceId: 'ws_demo_primary',
    title: 'Q4 Enterprise Product Requirements PRD',
    fileName: 'Q4_Enterprise_Product_Requirements_PRD.md',
    fileType: 'markdown',
    fileSizeBytes: 348000,
    formattedSize: '348 KB',
    status: 'active',
    chunkCount: 12,
    embeddingDimensions: 1536,
    embeddingModel: '@cf/baai/bge-large-en-v1.5',
    r2StorageKey: 'rag/ws_demo_primary/Q4_Enterprise_Product_Requirements_PRD.md',
    vectorizeIndexName: 'sophia-knowledge-1536',
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    sampleChunks: [
      {
        id: 'chunk_prd_01',
        documentId: 'doc_q4_prd',
        chunkIndex: 0,
        tokenCount: 380,
        content:
          'Mục tiêu Q4: Hoàn thiện hệ sinh thái AI Đa Phương Thức bao gồm Conversational Studio với luồng SSE, Tiptap Rich AI Editor, và bộ tạo hình ảnh Flux 1.1 Pro. Đảm bảo trải nghiệm người dùng đạt thời gian tải dưới 1 giây.',
        metadata: { heading: 'Mục Tiêu Chiến Lược Q4', wordCount: 52 },
      },
    ],
  },
  {
    id: 'doc_faq_database',
    workspaceId: 'ws_demo_primary',
    title: 'Customer Support FAQ Database v3',
    fileName: 'Customer_Support_FAQ_Database_v3.csv',
    fileType: 'csv',
    fileSizeBytes: 590000,
    formattedSize: '590 KB',
    status: 'active',
    chunkCount: 36,
    embeddingDimensions: 1536,
    embeddingModel: '@cf/baai/bge-large-en-v1.5',
    r2StorageKey: 'rag/ws_demo_primary/Customer_Support_FAQ_Database_v3.csv',
    vectorizeIndexName: 'sophia-knowledge-1536',
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    sampleChunks: [
      {
        id: 'chunk_faq_01',
        documentId: 'doc_faq_database',
        chunkIndex: 0,
        tokenCount: 420,
        content:
          'Q: Làm thế nào để nạp thêm tín chỉ Credits vào ví? A: Người dùng có thể truy cập mục Billing & Wallet trên thanh điều hướng, chọn gói nạp tín chỉ qua Stripe hoặc thẻ ngân hàng nội địa để nhận tín chỉ tức thời.',
        metadata: { heading: 'Câu Hỏi Thường Gặp Về Tín Chỉ', wordCount: 55 },
      },
    ],
  },
  {
    id: 'doc_api_specs_draft',
    workspaceId: 'ws_demo_primary',
    title: 'Draft Internal API Specs v2',
    fileName: 'Draft_Internal_API_Specs.txt',
    fileType: 'txt',
    fileSizeBytes: 122000,
    formattedSize: '122 KB',
    status: 'vectorizing',
    chunkCount: 6,
    embeddingDimensions: 1536,
    embeddingModel: '@cf/baai/bge-large-en-v1.5',
    r2StorageKey: 'rag/ws_demo_primary/Draft_Internal_API_Specs.txt',
    vectorizeIndexName: 'sophia-knowledge-1536',
    createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
    sampleChunks: [
      {
        id: 'chunk_api_01',
        documentId: 'doc_api_specs_draft',
        chunkIndex: 0,
        tokenCount: 350,
        content:
          'Draft endpoint: POST /api/v1/workspaces/:workspaceId/rag/vectorize - Đang xử lý phân mảnh và sinh embedding vector Float32...',
        metadata: { heading: 'API Endpoints In-progress', wordCount: 40 },
      },
    ],
  },
];

const loadStoredDocuments = (): KnowledgeDocumentItem[] => {
  if (typeof window === 'undefined') return INITIAL_KNOWLEDGE_DOCUMENTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_KNOWLEDGE);
    if (!raw) return INITIAL_KNOWLEDGE_DOCUMENTS;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : INITIAL_KNOWLEDGE_DOCUMENTS;
  } catch {
    return INITIAL_KNOWLEDGE_DOCUMENTS;
  }
};

const saveStoredDocuments = (docs: KnowledgeDocumentItem[]) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY_KNOWLEDGE, JSON.stringify(docs));
  } catch {
    // Ignore storage quota
  }
};

export const useKnowledgeStore = create<KnowledgeState>((set, get) => ({
  documents: loadStoredDocuments(),
  searchQuery: '',
  filterStatus: 'all',
  filterType: 'all',
  isUploadModalOpen: false,
  selectedPreviewDoc: null,
  activeReindexingId: null,
  reindexProgress: 0,

  addDocuments: (newDocs) => {
    const { documents } = get();
    const updated = [...newDocs, ...documents];
    saveStoredDocuments(updated);
    set({ documents: updated });
  },

  updateDocumentStatus: (id, status, errorMessage) => {
    const { documents } = get();
    const updated = documents.map((doc) =>
      doc.id === id
        ? {
            ...doc,
            status,
            errorMessage: errorMessage ?? doc.errorMessage,
            updatedAt: new Date().toISOString(),
          }
        : doc
    );
    saveStoredDocuments(updated);
    set({ documents: updated });
  },

  deleteDocument: (id) => {
    const { documents, selectedPreviewDoc } = get();
    const updated = documents.filter((d) => d.id !== id);
    saveStoredDocuments(updated);
    set({
      documents: updated,
      selectedPreviewDoc: selectedPreviewDoc?.id === id ? null : selectedPreviewDoc,
    });
  },

  reindexDocument: async (id) => {
    const { documents } = get();
    const target = documents.find((d) => d.id === id);
    if (!target) return;

    // Set status to chunking -> vectorizing -> active
    set({
      activeReindexingId: id,
      reindexProgress: 10,
    });

    // 1. Chunking phase
    get().updateDocumentStatus(id, 'chunking');
    await new Promise((r) => setTimeout(r, 400));
    set({ reindexProgress: 45 });

    // 2. Vectorizing phase
    get().updateDocumentStatus(id, 'vectorizing');
    await new Promise((r) => setTimeout(r, 600));
    set({ reindexProgress: 85 });

    // 3. Upsert to Vectorize index & finalize
    await new Promise((r) => setTimeout(r, 400));
    get().updateDocumentStatus(id, 'active');
    set({
      activeReindexingId: null,
      reindexProgress: 100,
    });

    // Reset progress indicator after a moment
    setTimeout(() => {
      set({ reindexProgress: 0 });
    }, 1000);
  },

  openChunkPreview: (doc) => {
    set({ selectedPreviewDoc: doc });
  },

  closeChunkPreview: () => {
    set({ selectedPreviewDoc: null });
  },

  openUploadModal: () => {
    set({ isUploadModalOpen: true });
  },

  closeUploadModal: () => {
    set({ isUploadModalOpen: false });
  },

  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setFilterStatus: (filterStatus) => set({ filterStatus }),
  setFilterType: (filterType) => set({ filterType }),

  resetStore: () => {
    saveStoredDocuments(INITIAL_KNOWLEDGE_DOCUMENTS);
    set({
      documents: [...INITIAL_KNOWLEDGE_DOCUMENTS],
      searchQuery: '',
      filterStatus: 'all',
      filterType: 'all',
      isUploadModalOpen: false,
      selectedPreviewDoc: null,
      activeReindexingId: null,
      reindexProgress: 0,
    });
  },

  getFilteredDocuments: () => {
    const { documents, searchQuery, filterStatus, filterType } = get();
    const query = searchQuery.trim().toLowerCase();

    return documents.filter((doc) => {
      // 1. Status Filter
      if (filterStatus !== 'all' && doc.status !== filterStatus) {
        return false;
      }

      // 2. File Type Filter
      if (filterType !== 'all' && doc.fileType !== filterType) {
        return false;
      }

      // 3. Search Query Filter
      if (query) {
        const titleMatch = doc.title.toLowerCase().includes(query);
        const fileNameMatch = doc.fileName.toLowerCase().includes(query);
        const chunkMatch = doc.sampleChunks.some((c) => c.content.toLowerCase().includes(query));
        return titleMatch || fileNameMatch || chunkMatch;
      }

      return true;
    });
  },

  getTotalStorageBytes: () => {
    const { documents } = get();
    return documents.reduce((sum, d) => sum + (d.fileSizeBytes || 0), 0);
  },

  getTotalChunksCount: () => {
    const { documents } = get();
    return documents.reduce((sum, d) => sum + (d.chunkCount || 0), 0);
  },
}));
